import { Dispatch, RefObject, SetStateAction } from 'react';
import { Customer } from '../../../api/customer';

export type TFunc = (key: string) => string;

// Max characters allowed in the free-text "assignment period" field.
export const ASSIGNMENT_PERIOD_MAX_LENGTH = 100;

// Max characters allowed in the free-text "offered services" field.
export const OFFERED_SERVICES_MAX_LENGTH = 500;

// Shared base every step receives
export interface StepBaseProps {
    t: TFunc;
    errors: Record<string, string>;
    setErrors: Dispatch<SetStateAction<Record<string, string>>>;
}

// ── Step 0 — Company Logo & Profile ──────────────────────────────────────────

export interface Step0Props extends StepBaseProps {
    logoUrl: string | null;
    personalData: PersonalDataSnapshot;
    lang: string;
    onLangChange: (lang: string) => void;
    onNavigateToProfile: () => void;
}

// ── Step 1 — Parties Information ─────────────────────────────────────────────

export interface Step1Props extends StepBaseProps {
    personalData: PersonalDataSnapshot;
    customers: Customer[];
    selectedCustomerId: number | null;
    setSelectedCustomerId: Dispatch<SetStateAction<number | null>>;
    showCustomerDropdown: boolean;
    setShowCustomerDropdown: Dispatch<SetStateAction<boolean>>;
    offeredServices: string;
    setOfferedServices: Dispatch<SetStateAction<string>>;
    assignmentPeriod: string;
    setAssignmentPeriod: Dispatch<SetStateAction<string>>;
    workDescription: string;
    setWorkDescription: Dispatch<SetStateAction<string>>;
    specialTerms: string;
    setSpecialTerms: Dispatch<SetStateAction<string>>;
    onCustomerAdded: () => void;
}

// ── Step 2 — Assignment & Compensation (merged) ───────────────────────────────

export interface Step2Props extends StepBaseProps {
    // Assignment
    startDate: string;
    setStartDate: Dispatch<SetStateAction<string>>;
    endDate: string;
    setEndDate: Dispatch<SetStateAction<string>>;
    noEndDate: boolean;
    setNoEndDate: Dispatch<SetStateAction<boolean>>;
    hasNoticePeriod: boolean;
    setHasNoticePeriod: Dispatch<SetStateAction<boolean>>;
    noticePeriod: string;
    setNoticePeriod: Dispatch<SetStateAction<string>>;
    assignmentDesc: string;
    setAssignmentDesc: Dispatch<SetStateAction<string>>;
    // Compensation
    compType: string;
    setCompType: Dispatch<SetStateAction<string>>;
    unit: string;
    setUnit: Dispatch<SetStateAction<string>>;
    currency: string;
    setCurrency: Dispatch<SetStateAction<string>>;
    amount: string;
    setAmount: Dispatch<SetStateAction<string>>;
}

// ── Step 3 — Legal & Signatures ───────────────────────────────────────────────

export interface Step3Props extends StepBaseProps {
    hasAgreed: boolean;
    setHasAgreed: Dispatch<SetStateAction<boolean>>;
    signDate: string;
    signerName: string;
    setSignerName: Dispatch<SetStateAction<string>>;
    hasSignature: boolean;
    setHasSignature: Dispatch<SetStateAction<boolean>>;
    canvasRef: RefObject<HTMLCanvasElement>;
    initialSignatureData?: string;
    // The customer signs later via the emailed sign-contract link, not in
    // this wizard — these just drive the read-only "pending/signed" panel.
    customerName?: string;
    isCustomerSigned?: boolean;
}

// ── Shared snapshot of profile data used across steps ────────────────────────

export interface PersonalDataSnapshot {
    firstName?: string;
    lastName?: string;
    email?: string;
    address?: string;
    city?: string;
    zipCode?: string;
}

export const buildFullName = (p?: PersonalDataSnapshot): string =>
    [p?.firstName, p?.lastName].filter(Boolean).join(' ') || '—';

export const buildAddress = (p?: PersonalDataSnapshot): string =>
    [p?.address, p?.city, p?.zipCode].filter(Boolean).join(', ') || '—';
